import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import SgqNc from '../../../../../ui-component/sgq/SgqNc';
import api from '../../../../services/api';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    confirmed: { label: 'Confirmado', color: 'success' },
    cancelled: { label: 'Cancelado', color: 'error' }
};

const RECEIPT_STATUS_LABEL = {
    pending_receipt: { label: 'Aguardando recebimento', color: 'default' },
    partially_received: { label: 'Parcialmente recebido', color: 'warning' },
    received: { label: 'Recebido', color: 'success' }
};

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const formatDateBR = (value) => {
    if (!value) return '-';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
};

const ViewPurchaseOrder = () => {
    const { id } = useParams();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [order, setOrder] = useState(null);
    const [receipts, setReceipts] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [working, setWorking] = useState(false);

    const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
    const [receiptDrafts, setReceiptDrafts] = useState({});
    const [savingReceipt, setSavingReceipt] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        // Em transições de rota (ex: saindo de "/dashboard/purchasing/new",
        // "/dashboard/purchasing/requests" ou "/dashboard/purchasing/quotations/<id>"
        // - rotas irmãs desta, adicionadas junto com Solicitações/Cotações
        // de Compra), essa tela pode remontar brevemente com `id` ainda não
        // resolvido pro UUID real - sem essa guarda, dispara um GET inválido
        // (".../purchase_orders/new/", ".../purchase_orders/requests/" ou
        // ".../purchase_orders/quotations/") que sempre dá erro (o id não
        // é um UUID válido de PurchaseOrder).
        if (!appId || !id || id === 'new' || id === 'requests' || id === 'quotations') return;
        carregarPedido();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    const carregarPedido = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/purchase_orders/${id}/`);
            setOrder(res.data);
            if (res.data.status === 'confirmed') {
                carregarRecebimentos();
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar pedido' });
        }
    };

    const carregarRecebimentos = async () => {
        const res = await api.get(`v1/apps/${appId}/purchase_orders/${id}/goods-receipts/`);
        setReceipts(res.data.receipts || []);
    };

    const handleConfirm = async () => {
        if (!window.confirm('Confirmar este pedido? Isso vai gerar uma conta a pagar. O recebimento físico da mercadoria é registrado depois, separadamente.')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/purchase_orders/${id}/confirm/`, {});
            setOrder(res.data);
            setStatus({ type: 'success', message: 'Pedido confirmado com sucesso!' });
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao confirmar pedido' });
        } finally {
            setWorking(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Cancelar este pedido?')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/purchase_orders/${id}/cancel/`, {});
            setOrder(res.data);
            setStatus({ type: 'success', message: 'Pedido cancelado.' });
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao cancelar pedido' });
        } finally {
            setWorking(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Excluir este pedido em rascunho? Essa ação não pode ser desfeita.')) return;
        setWorking(true);
        try {
            await api.delete(`v1/apps/${appId}/purchase_orders/${id}/`);
            history.push('/dashboard/purchasing');
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir pedido' });
            setWorking(false);
        }
    };

    const openReceiptDialog = () => {
        const drafts = {};
        (order.items || []).forEach((item) => {
            if (item.produto_variacao && Number(item.quantidade_pendente) > 0) {
                drafts[item.id] = {
                    quantidade_recebida: item.quantidade_pendente,
                    lot_number: '',
                    lot_validade: '',
                    serial_numbers: ''
                };
            }
        });
        setReceiptDrafts(drafts);
        setReceiptDialogOpen(true);
    };

    const handleSaveReceipt = async () => {
        const items = Object.entries(receiptDrafts)
            .filter(([, draft]) => Number(draft.quantidade_recebida) > 0)
            .map(([orderItemId, draft]) => {
                const orderItem = order.items.find((i) => i.id === orderItemId);
                const payload = { order_item: orderItemId, quantidade_recebida: Number(draft.quantidade_recebida) };
                if (orderItem?.controle_estoque === 'lot') {
                    payload.lot_number = draft.lot_number || undefined;
                    payload.lot_validade = draft.lot_validade || undefined;
                } else if (orderItem?.controle_estoque === 'serial') {
                    payload.serial_numbers = draft.serial_numbers
                        .split(/[\n,]/)
                        .map((s) => s.trim())
                        .filter(Boolean);
                }
                return payload;
            });

        if (items.length === 0) {
            setStatus({ type: 'error', message: 'Informe ao menos uma quantidade recebida maior que zero' });
            return;
        }

        setSavingReceipt(true);
        try {
            await api.post(`v1/apps/${appId}/purchase_orders/${id}/goods-receipts/`, { items });
            setReceiptDialogOpen(false);
            setStatus({ type: 'success', message: 'Recebimento registrado com sucesso.' });
            await carregarPedido();
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao registrar recebimento' });
        } finally {
            setSavingReceipt(false);
        }
    };

    const handleReverseReceipt = async (receiptId) => {
        if (!window.confirm('Estornar este recebimento? Isso vai dar saída no estoque do que foi recebido.')) return;
        try {
            await api.post(`v1/apps/${appId}/purchase_orders/${id}/goods-receipts/${receiptId}/reverse/`, {});
            await carregarPedido();
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao estornar recebimento' });
        }
    };

    if (!order) {
        return <Box>{status.message && <Alert severity={status.type}>{status.message}</Alert>}</Box>;
    }

    const statusInfo = STATUS_LABEL[order.status] || { label: order.status, color: 'default' };
    const receiptStatusInfo = order.receipt_status ? RECEIPT_STATUS_LABEL[order.receipt_status] : null;
    const hasPendingItems = (order.items || []).some((item) => item.produto_variacao && Number(item.quantidade_pendente) > 0);

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h3">Pedido de Compra</Typography>
                <Stack direction="row" spacing={1}>
                    <Chip label={statusInfo.label} color={statusInfo.color} />
                    {receiptStatusInfo && <Chip label={receiptStatusInfo.label} color={receiptStatusInfo.color} variant="outlined" />}
                    <SgqNc appId={appId} entityType="supplier" entityId={order.organization} defaultTitle={`Problema com o fornecedor ${order.organization_name}`} />
                </Stack>
            </Stack>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Fornecedor
                            </Typography>
                            <Typography>{order.organization_name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Data do pedido
                            </Typography>
                            <Typography>{order.order_date}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Depósito
                            </Typography>
                            <Typography>{order.warehouse_name || 'Depósito principal (padrão)'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Conta a pagar gerada
                            </Typography>
                            {order.accounts_payable ? (
                                <RouterLink to={`/dashboard/financial/payable/${order.accounts_payable}/edit`}>
                                    Ver conta a pagar
                                </RouterLink>
                            ) : (
                                <Typography>-</Typography>
                            )}
                        </Grid>
                        {order.notes && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Observações
                                </Typography>
                                <Typography>{order.notes}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Produto</TableCell>
                                <TableCell align="right">Quantidade</TableCell>
                                <TableCell align="right">Custo unitário</TableCell>
                                <TableCell align="right">Subtotal</TableCell>
                                {order.status === 'confirmed' && (
                                    <>
                                        <TableCell align="right">Recebido</TableCell>
                                        <TableCell align="right">Pendente</TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {order.items?.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.produto_variacao_nome || item.service_nome}</TableCell>
                                    <TableCell align="right">{item.quantidade}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.valor_unitario)}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.subtotal)}</TableCell>
                                    {order.status === 'confirmed' && (
                                        <>
                                            <TableCell align="right">{item.produto_variacao ? item.quantidade_recebida : '-'}</TableCell>
                                            <TableCell align="right">{item.produto_variacao ? item.quantidade_pendente : '-'}</TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box textAlign="right" mt={2}>
                        <Typography variant="h5">Total: {formatCurrencyBR(order.total_amount)}</Typography>
                    </Box>
                </CardContent>
            </Card>

            {order.status === 'confirmed' && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h5">Recebimentos</Typography>
                            {hasPendingItems && (
                                <Button variant="outlined" onClick={openReceiptDialog}>
                                    Registrar Recebimento
                                </Button>
                            )}
                        </Stack>

                        {receipts.length === 0 ? (
                            <Typography color="text.secondary">Nenhum recebimento registrado ainda.</Typography>
                        ) : (
                            receipts.map((receipt) => (
                                <Box key={receipt.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatDateBR(receipt.received_at)}
                                        </Typography>
                                        <SgqNc appId={appId} entityType="goods_receipt" entityId={receipt.id} defaultTitle="Material reprovado no recebimento" />
                                        {receipt.is_reversed ? (
                                            <Chip label="Estornado" size="small" />
                                        ) : (
                                            <Button size="small" color="warning" onClick={() => handleReverseReceipt(receipt.id)}>
                                                Estornar
                                            </Button>
                                        )}
                                    </Stack>
                                    <Table size="small" sx={{ mt: 1 }}>
                                        <TableBody>
                                            {receipt.items.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell sx={{ textDecoration: receipt.is_reversed ? 'line-through' : 'none' }}>
                                                        {item.produto_variacao_nome}
                                                        {item.lot_number && ` (lote ${item.lot_number})`}
                                                    </TableCell>
                                                    <TableCell align="right">{item.quantidade_recebida}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            ))
                        )}
                    </CardContent>
                </Card>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={() => history.push('/dashboard/purchasing')}>Voltar</Button>

                {order.status === 'draft' && (
                    <Button color="error" variant="outlined" disabled={working} onClick={handleDelete}>
                        Excluir
                    </Button>
                )}

                {(order.status === 'draft' || order.status === 'confirmed') && (
                    <Button color="warning" variant="outlined" disabled={working} onClick={handleCancel}>
                        Cancelar Pedido
                    </Button>
                )}

                {order.status === 'draft' && (
                    <Button variant="contained" disabled={working} onClick={handleConfirm}>
                        Confirmar Pedido
                    </Button>
                )}
            </Box>

            <Dialog open={receiptDialogOpen} onClose={() => setReceiptDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Registrar Recebimento</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {Object.keys(receiptDrafts).map((orderItemId) => {
                            const item = order.items.find((i) => i.id === orderItemId);
                            const draft = receiptDrafts[orderItemId];
                            return (
                                <Box key={orderItemId} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                                        {item.produto_variacao_nome} (pendente: {item.quantidade_pendente})
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                label="Quantidade recebida"
                                                type="number"
                                                fullWidth
                                                inputProps={{ min: 0, max: item.quantidade_pendente }}
                                                value={draft.quantidade_recebida}
                                                onChange={(e) =>
                                                    setReceiptDrafts({
                                                        ...receiptDrafts,
                                                        [orderItemId]: { ...draft, quantidade_recebida: e.target.value }
                                                    })
                                                }
                                            />
                                        </Grid>
                                        {item.controle_estoque === 'lot' && (
                                            <>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        label="Número do lote"
                                                        fullWidth
                                                        value={draft.lot_number}
                                                        onChange={(e) =>
                                                            setReceiptDrafts({
                                                                ...receiptDrafts,
                                                                [orderItemId]: { ...draft, lot_number: e.target.value }
                                                            })
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        label="Validade (opcional)"
                                                        type="date"
                                                        fullWidth
                                                        InputLabelProps={{ shrink: true }}
                                                        value={draft.lot_validade}
                                                        onChange={(e) =>
                                                            setReceiptDrafts({
                                                                ...receiptDrafts,
                                                                [orderItemId]: { ...draft, lot_validade: e.target.value }
                                                            })
                                                        }
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                        {item.controle_estoque === 'serial' && (
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Números de série"
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                    helperText={`Um por linha (ou separados por vírgula) - precisa ter exatamente ${draft.quantidade_recebida || 0} número(s)`}
                                                    value={draft.serial_numbers}
                                                    onChange={(e) =>
                                                        setReceiptDrafts({
                                                            ...receiptDrafts,
                                                            [orderItemId]: { ...draft, serial_numbers: e.target.value }
                                                        })
                                                    }
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            );
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReceiptDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSaveReceipt} disabled={savingReceipt}>
                        {savingReceipt ? 'Salvando...' : 'Registrar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ViewPurchaseOrder;
